import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Search, Mail, Phone, MapPin, Building2, StickyNote, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPhoneDisplay } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceProviders = () => {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [spPage, setSpPage] = useState(1);
  const [spPageSize, setSpPageSize] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersRes, serviceTypesRes] = await Promise.all([
        axios.get(`${API}/agent/service-providers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/provider-pools/service-types`)
      ]);

      setProviders(providersRes.data);
      setServiceTypes(serviceTypesRes.data.service_types || []);
    } catch (error) {
      toast.error('Failed to load service providers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(provider => {
    const matchesSearch = 
      provider.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.state.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Check if filterType is in provider's service_types array (for multi-service providers)
    let matchesType = filterType === 'all';
    if (!matchesType && provider.service_types && Array.isArray(provider.service_types)) {
      matchesType = provider.service_types.includes(filterType);
    } else if (!matchesType && provider.service_type) {
      // Fallback for backward compatibility with single service_type string
      matchesType = provider.service_type === filterType;
    }
    
    return matchesSearch && matchesType;
  });

  useEffect(() => {
    setSpPage(1);
  }, [searchQuery, filterType]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading service providers...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Service Providers
          </h1>
          <p className="text-stone-600">Manage your network of trusted service professionals</p>
        </div>

        {/* Search and Filter */}
        <Card className="bg-white p-6 rounded-2xl shadow-lg">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={20} />
              <Input
                placeholder="Search by name, company, email, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
              />
            </div>

            {/* Filter by Service Type */}
            <div className="md:w-64">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="All Service Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Service Types</SelectItem>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.id || type} value={type.name || type}>
                      {type.name || type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-stone-600">
              Showing {Math.min((spPage - 1) * spPageSize + 1, filteredProviders.length)}-{Math.min(spPage * spPageSize, filteredProviders.length)} of {filteredProviders.length} providers
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-stone-500">Per page:</span>
                <Select value={String(spPageSize)} onValueChange={(v) => { setSpPageSize(Number(v)); setSpPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={spPage <= 1} onClick={() => setSpPage(p => p - 1)}>
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-xs font-medium text-stone-700 px-2">{spPage}/{Math.max(1, Math.ceil(filteredProviders.length / spPageSize))}</span>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={spPage >= Math.ceil(filteredProviders.length / spPageSize)} onClick={() => setSpPage(p => p + 1)}>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Providers List */}
        {filteredProviders.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl shadow-lg text-center">
            <Briefcase className="mx-auto mb-4 text-stone-300" size={64} />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">
              {providers.length === 0 ? 'No Service Providers Yet' : 'No Matches Found'}
            </h3>
            <p className="text-stone-500 mb-4">
              {providers.length === 0 
                ? 'Share your QR code from Settings to start building your network'
                : 'Try adjusting your search or filter criteria'}
            </p>
            {providers.length === 0 && (
              <Button
                onClick={() => window.location.href = '/settings'}
                className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
              >
                Go to Settings
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.slice((spPage - 1) * spPageSize, spPage * spPageSize).map((provider) => (
              <Card key={provider.id} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                      <Briefcase className="text-[#1A4D2E]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-stone-800">{provider.full_name}</h3>
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#1A4D2E]/10 text-[#1A4D2E] mt-1">
                        {provider.service_type}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Building2 size={16} className="text-stone-400 flex-shrink-0" />
                    <span className="font-medium">{provider.company_name}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Phone size={16} className="text-stone-400 flex-shrink-0" />
                    <a href={`tel:${provider.phone}`} className="hover:text-[#1A4D2E] hover:underline">
                      {formatPhoneDisplay(provider.phone)}
                    </a>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Mail size={16} className="text-stone-400 flex-shrink-0" />
                    <a href={`mailto:${provider.email}`} className="hover:text-[#1A4D2E] hover:underline truncate">
                      {provider.email}
                    </a>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <MapPin size={16} className="text-stone-400 flex-shrink-0" />
                    <span>{provider.city}, {provider.state}</span>
                  </div>

                  {provider.notes && (
                    <div className="flex items-start gap-2 text-sm text-stone-600 pt-2 border-t border-stone-100">
                      <StickyNote size={16} className="text-stone-400 flex-shrink-0 mt-0.5" />
                      <span className="italic">{provider.notes}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-stone-100">
                  <p className="text-xs text-stone-400">
                    Registered {new Date(provider.created_at).toLocaleDateString()}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ServiceProviders;
